import { useState, useEffect, memo, useRef } from "react"
import { useShallow } from "zustand/react/shallow"
import { SegmentedButtons } from "components-design-system"
import CommentsPanel from "../comments/CommentsPanel/CommentsPanel"
import ValidationPanel from "../ValidationPanel/ValidationPanel"
import CollapsePanelButton from "components/CollapsePanelButton/CollapsePanelButton"
import { Affix, Flex } from "antd"
import { SCROLLABLE_AUTHORING_CONTAINER } from "constants/authoring"

// hooks
import useAuthoringViewStore from "../../hooks/stores/useAuthoringViewStore"
import useAuthoringValidationsStore from "../../hooks/stores/useAuthoringValidationsStore"

// styles
import "./SidePanelContainer.scss"

const SidePanelContainer = () => {
  const sidePanelRef = useRef()

  const { isReviewerView, showPanel, setShowPanel, fullScreen, setPanelView, panelView } = useAuthoringViewStore(
    useShallow((state) => ({
      isReviewerView: state.isReviewerView,
      showPanel: state.showPanel,
      setShowPanel: state.setShowPanel,
      fullScreen: state.fullScreen,
      panelView: state.panelView,
      setPanelView: state.setPanelView,
    }))
  )

  const { validations } = useAuthoringValidationsStore(
    useShallow((state) => ({
      validations: state.validations,
    }))
  )

  const [showCollapse, setShowCollapse] = useState(false)

  useEffect(() => {
    if (isReviewerView !== true) {
      localStorage.setItem("SHOW_PANEL", showPanel)
    }
  }, [showPanel])

  useEffect(() => {
    if (validations?.unmappedValidations?.length > 0) {
      setShowPanel(true)
      setPanelView("validations")
    }
  }, [validations?.unmappedValidations])

  const onTogglePanel = () => {
    const show = !showPanel
    setShowPanel(show)

    const parentElement = sidePanelRef.current.parentElement.parentElement

    if (parentElement) {
      // Get all child elements of the parent element
      const childElements = parentElement.querySelectorAll("*")

      // Loop through each child element
      for (let i = 0; i < childElements.length; i++) {
        const childElement = childElements[i]
        // Check if the child element has the aria-hidden attribute set to true
        if (childElement.getAttribute("aria-hidden") === "true" && childElement.className === "") {
          // This is the child element you are looking for
          childElement.style.width = show ? "347px" : "16px"
          break
        }
      }
    }
  }

  let classNames = ["side-panel-container"]
  let collasePanelButtonClassNames = ["auth-form-collapse-comment-container"]

  if (showPanel) {
    classNames.push("--show")
  } else {
    collasePanelButtonClassNames.push("auth-form-collapse-comment-closed")
    classNames.push("--hidden")
  }

  if (isReviewerView) {
    classNames.push("reviewer-view")
  }

  collasePanelButtonClassNames.push("auth-form-collapse-panel")

  if (showCollapse === false && showPanel === true) collasePanelButtonClassNames.push("hidden")
  if (fullScreen) return null

  return (
    <Affix
      target={() => document.getElementsByClassName(SCROLLABLE_AUTHORING_CONTAINER.FORM_CLASSNAME)[0]}
      offsetTop={44}
      style={{ zIndex: 2 }}
    >
      <Flex
        ref={sidePanelRef}
        className={classNames.join(" ")}
        data-tour={`study-data-side-panel${showPanel ? "-open" : "-closed"}`}
        onMouseEnter={() => setShowCollapse(true)}
        onMouseLeave={() => setShowCollapse(false)}
      >
        <CollapsePanelButton
          open={showPanel}
          placement="right"
          className={collasePanelButtonClassNames.join(" ")}
          setOpen={onTogglePanel}
        />
        <Flex
          className={showPanel ? "inner--shown" : "inner--hidden"}
          vertical
          gap={16}
        >
          {!isReviewerView && (
            <SegmentedButtons
              options={[
                {
                  label: "Comments",
                  value: "comments",
                  icon: <i className="fal fa-comment-alt-dots" />,
                  style: { width: "50%" },
                },
                {
                  label: "Validations",
                  value: "validations",
                  icon: <i className="fa-sharp fa-light fa-circle-exclamation"></i>,
                  style: { width: "50%" },
                },
              ].filter((item) => {
                if (isReviewerView && item.value === "validations") return false
                return true
              })}
              onChange={(val) => {
                setPanelView(val)
              }}
              value={panelView}
            />
          )}
          {panelView === "comments" && <CommentsPanel />}
          {panelView === "validations" && (
            <ValidationPanel
              style={{ float: "none" }}
              validations={validations}
            />
          )}
        </Flex>
      </Flex>
    </Affix>
  )
}

export default SidePanelContainer
